import { useState, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "next-themes";
import { ChevronDown, ChevronRight, LayoutDashboard, Package, Rocket, Settings, LogOut, Sun, Moon, Search, HardDrive, RefreshCw, Users, FileText, Wrench, Crown, Shield, Key, Cloud, Database, SlidersHorizontal, ShieldAlert, PanelLeftClose } from "lucide-react";
import axios from "axios";
import TenantSelector from "./TenantSelector";

const Sidebar = ({ onHide }) => {
  const { user, logout, demoMode, getAccessToken } = useAuth();
  // Light / Dark switch (October 2026). next-themes keeps the choice per browser.
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [expandedSections, setExpandedSections] = useState({ appsManagement: true, osPatchManagement: false, settings: false });
  const [userPermissions, setUserPermissions] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // Tenant capabilities from /users/me. null = unknown -> permissive.
  const [tenantCapabilities, setTenantCapabilities] = useState(null);
  // Service tier from /users/me. null = not reported (older backend).
  const [serviceTier, setServiceTier] = useState(null);
  
  const toggleSection = (section) => setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  const isActive = (path) => location.pathname === path;
  const isInSection = (paths) => paths.some((p) => location.pathname.startsWith(p));

  // Auto-expand Settings section when on /settings
  useEffect(() => {
    if (location.pathname.startsWith("/settings")) {
      setExpandedSections((prev) => (prev.settings ? prev : { ...prev, settings: true }));
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchUserPermissions = async () => {
      if (demoMode) {
        setUserPermissions([
          "view_apps", "manage_apps", "create_packages", "deploy_packages",
          "view_os_patches", "manage_os_patches", "view_settings", "manage_settings",
          "manage_users", "view_users", "view_dashboard"
        ]);
        setUserRole("tenant_admin");
        return;
      }
      
      try {
        const token = await getAccessToken();
        if (!token) return;
        
        const response = await axios.get(`${API}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setUserPermissions(response.data.permissions || []);
        setUserRole(response.data.role);
        setIsSuperAdmin(response.data.is_super_admin || false);
        // null = unknown (older backend, or not yet loaded) -> every
        // capability granted, so nothing disappears for Intune tenants.
        setTenantCapabilities(
          Array.isArray(response.data.capabilities) ? response.data.capabilities : null
        );
        setServiceTier(response.data.service_tier || null);
      } catch (error) {
        console.error("Failed to fetch user permissions:", error);
        setUserPermissions(["view_apps", "view_os_patches", "view_settings", "view_dashboard"]);
      }
    };
    
    fetchUserPermissions();
  }, [demoMode, getAccessToken]);

  const hasPermission = (permission) => {
    if (!userPermissions) return true;
    return userPermissions.includes(permission);
  };

  // Tenant capability = what the ONBOARDING MODE allows, a separate axis
  // from the user's role. An SCCM tenant has no Intune connection, so
  // Deployments and OS Patch are not merely forbidden -- they do not
  // exist for that tenant. Unknown (null) resolves permissive.
  const hasCapability = (capability) => {
    if (!tenantCapabilities) return true;
    return tenantCapabilities.includes(capability);
  };

  const canViewApps = () => hasPermission("view_apps");
  const canViewOSPatches = () =>
    hasPermission("view_os_patches") && hasCapability("os_patch_management");
  // Update rings, feature updates, deployments and remediation. The
  // reporting-only plan doesn't include them. Only withheld when the backend
  // reports a service tier, so an older backend never hides these entries.
  const canViewOSPatchPolicies = () =>
    !serviceTier || hasCapability("os_patch_policies");
  const canViewDeployments = () => hasCapability("intune_deployment");
  const canImportSccm = () => hasCapability("sccm_import");

  const getRoleBadge = () => {
    if (isSuperAdmin) return { label: "Super Admin", color: "bg-[#3C02CC] text-white" };
    switch (userRole) {
      case "tenant_admin": return { label: "Admin", color: "bg-[#FC5442] text-white" };
      case "apps_manager": return { label: "Apps", color: "bg-[#006AF2] text-white" };
      case "os_patch_manager": return { label: "OS Patch", color: "bg-[#BBFF71] text-[#220175]" };
      case "viewer": return { label: "Viewer", color: "bg-[#220175]/10 text-[#220175]" };
      default: return null;
    }
  };

  const NavItem = ({ to, icon: Icon, label, indent = false }) => (
    <Link to={to}>
      <div className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150",
        indent && "ml-4",
        isActive(to)
          ? "bg-[#006AF2] text-white font-semibold shadow-md shadow-[#006AF2]/25"
          : "alice-ink-soft alice-nav"
      )} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <Icon className="h-4 w-4 flex-shrink-0" /><span>{label}</span>
      </div>
    </Link>
  );

  const SectionHeader = ({ label, icon: Icon, expanded, onClick, isActiveSection }) => (
    <button onClick={onClick} className={cn(
      "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs transition-all duration-150 whitespace-nowrap",
      isActiveSection
        ? "bg-[#006AF2]/10 text-[#006AF2] font-medium"
        : "alice-ink-soft alice-nav"
    )}>
      <div className="flex items-center gap-2"><Icon className="h-4 w-4 flex-shrink-0" /><span className="font-medium">{label}</span></div>
      {expanded ? <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />}
    </button>
  );

  // Sub-nav item for Settings sub-sections (keyed off ?section= search param)
  const currentSettingsSection = searchParams.get("section") || "general";
  const SettingsNavItem = ({ section, icon: Icon, label }) => {
    const active = location.pathname === "/settings" && currentSettingsSection === section;
    return (
      <Link to={`/settings?section=${section}`}>
        <div className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ml-4",
          active
            ? "bg-[#006AF2] text-white font-semibold shadow-md shadow-[#006AF2]/25"
            : "alice-ink-soft alice-nav"
        )} data-testid={`nav-settings-${section}`}>
          <Icon className="h-4 w-4 flex-shrink-0" /><span>{label}</span>
        </div>
      </Link>
    );
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="alice-sidebar w-64 h-screen sticky top-0 flex flex-col shrink-0 relative z-10" data-zone="sidebar">
      {/* Logo */}
      <div className="p-4 border-b alice-sidebar-divider">
        {/* The real ALICE logo: indigo wordmark in Light, white in Dark. The row is pinned at 40px
            so the nav below doesn't shift between modes. */}
        <div className="flex items-center">
          <h1 className="flex items-center h-10" aria-label="ALICE">
            <img src="/assets/theme/alice-logo-light.svg" alt="ALICE" className="alice-logo-light h-[34px] w-auto" />
            <img src="/assets/theme/alice-logo-dark.svg" alt="" aria-hidden="true" className="alice-logo-dark h-[34px] w-auto" />
          </h1>
          {onHide && (
            <Button variant="ghost" size="icon" onClick={onHide} className="alice-ink-faint alice-nav ml-auto h-8 w-8" title="Hide sidebar" aria-label="Hide sidebar" data-testid="sidebar-hide">
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Tenant Selector */}
      <div className="px-3 py-2 border-b alice-sidebar-divider">
        <TenantSelector />
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />
        
        {canViewApps() && (
          <div className="pt-2">
            <SectionHeader label="Apps Management" icon={Package} expanded={expandedSections.appsManagement} onClick={() => toggleSection("appsManagement")} isActiveSection={isInSection(["/apps", "/discover", "/packages", "/deployments", "/catalog", "/cve"])} />
            {expandedSections.appsManagement && (<div className="mt-1 space-y-0.5">
              <NavItem to="/apps/dashboard" icon={LayoutDashboard} label="Dashboard" indent />
              <NavItem to="/discover" icon={Search} label="Discover" indent />
              <NavItem to="/packages" icon={Package} label="Packages" indent />
              {canViewDeployments() && (
                <NavItem to="/deployments" icon={Rocket} label="Deployments" indent />
              )}
            </div>)}
          </div>
        )}
        
        {canViewOSPatches() && (
          <div className="pt-2">
            <SectionHeader label="OS Patch Management" icon={HardDrive} expanded={expandedSections.osPatchManagement} onClick={() => toggleSection("osPatchManagement")} isActiveSection={isInSection(["/os-patch"])} />
            {expandedSections.osPatchManagement && (<div className="mt-1 space-y-0.5">
              <NavItem to="/os-patch/dashboard" icon={LayoutDashboard} label="Dashboard" indent />
              <NavItem to="/os-patch/discover" icon={FileText} label="Discover" indent />
              {/* Devices moved into Discover > Devices (October 2026);
                  /os-patch/devices redirects there. */}
              {canViewOSPatchPolicies() && (<>
                <NavItem to="/os-patch/updates" icon={RefreshCw} label="Updates" indent />
                <NavItem to="/os-patch/groups" icon={Users} label="Deployments" indent />
                <NavItem to="/os-patch/remediation" icon={Wrench} label="Remediation" indent />
              </>)}
            </div>)}
          </div>
        )}
        
        <div className="pt-2">
          <SectionHeader label="Settings" icon={Settings} expanded={expandedSections.settings} onClick={() => toggleSection("settings")} isActiveSection={isInSection(["/settings"])} />
          {expandedSections.settings && (<div className="mt-1 space-y-0.5">
            <SettingsNavItem section="general" icon={SlidersHorizontal} label="General" />
            <SettingsNavItem section="access" icon={Users} label="Account & Access" />
            <SettingsNavItem section="integrations" icon={Key} label="Integrations" />
            <SettingsNavItem section="storage" icon={Cloud} label="Storage" />
            <SettingsNavItem section="automation" icon={Rocket} label="Automation" />
            <SettingsNavItem section="data" icon={Database} label="Data Management" />
            {isSuperAdmin && <SettingsNavItem section="admin" icon={Crown} label="Administration" />}
          </div>)}
        </div>
      </nav>
      
      {/* User Section */}
      <div className="p-3 border-t alice-sidebar-divider">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="relative">
            <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium bg-[#006AF2]">
              {user?.name?.charAt(0) || "U"}
            </div>
            {isSuperAdmin && (
              <Crown className="absolute -top-1 -right-1 h-4 w-4 text-yellow-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium truncate alice-ink">{user?.name || "User"}</p>
              {roleBadge && (
                <span className={cn("px-1.5 py-0.5 text-[10px] font-medium rounded", roleBadge.color)}>
                  {roleBadge.label}
                </span>
              )}
            </div>
            <p className="text-xs alice-ink-faint truncate">{user?.email || ""}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setTheme(isDark ? "light" : "dark")} className="alice-ink-faint alice-nav" title={isDark ? "Switch to Light mode" : "Switch to Dark mode"} aria-label={isDark ? "Switch to Light mode" : "Switch to Dark mode"} data-testid="theme-toggle">{isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</Button>
          <Button variant="ghost" size="icon" onClick={logout} className="alice-ink-faint alice-nav" data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
};
export default Sidebar;
